import { useRef, useState, type DragEvent, type ReactNode } from 'react'
import { messageFor } from './feedback.js'

/**
 * Several files at once, for the drives.
 *
 * A site office does not upload one drawing; it uploads the twenty-two sheets
 * of a revision, or the photographs of a pour. Picking them one at a time is
 * how three of them never arrive. So the picker takes many, a drop takes many,
 * and each goes through the same single-file upload the drive always used —
 * two at a time, so one large file does not hold up the rest and the server is
 * not asked for twenty at once.
 *
 * The collection or category showing when the files were chosen is the one
 * the whole batch is filed under: it is read when they are added, so changing
 * the filter half-way does not split a batch across two folders.
 */
export type UploadState = 'WAITING' | 'UPLOADING' | 'DONE' | 'FAILED'

export interface UploadItem {
  readonly id: string
  readonly name: string
  readonly size: number
  readonly state: UploadState
  readonly error: string | null
}

const CONCURRENCY = 2

export const useUploadQueue = (options: {
  readonly maxBytes: number
  /** Called after each file that went up, so the list fills as it goes. */
  readonly onUploaded?: () => void
}) => {
  const [items, setItems] = useState<UploadItem[]>([])
  const seq = useRef(0)

  const update = (id: string, change: Partial<UploadItem>): void =>
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...change } : item)))

  /** Queues the files; `upload` is the call for one file, fixed for this batch. */
  const add = (files: readonly File[], upload: (file: File) => Promise<unknown>): void => {
    if (files.length === 0) return
    const limitMb = Math.round(options.maxBytes / 1024 / 1024)
    const fresh = files.map((file) => {
      const tooBig = file.size > options.maxBytes
      return {
        file,
        item: {
          id: `upload-${++seq.current}`,
          name: file.name,
          size: file.size,
          state: tooBig ? 'FAILED' : 'WAITING',
          error: tooBig ? `Larger than the ${limitMb} MB limit` : null,
        } satisfies UploadItem,
      }
    })
    setItems((prev) => [...prev, ...fresh.map((f) => f.item)])

    const todo = fresh.filter((f) => f.item.state === 'WAITING')
    let next = 0
    const worker = async (): Promise<void> => {
      while (next < todo.length) {
        const entry = todo[next++]
        if (!entry) break
        update(entry.item.id, { state: 'UPLOADING' })
        try {
          await upload(entry.file)
          update(entry.item.id, { state: 'DONE' })
          options.onUploaded?.()
        } catch (err) {
          update(entry.item.id, { state: 'FAILED', error: messageFor(err) })
        }
      }
    }
    void Promise.all(Array.from({ length: Math.min(CONCURRENCY, todo.length) }, worker))
  }

  const busy = items.some((i) => i.state === 'WAITING' || i.state === 'UPLOADING')
  /** Forget the finished ones; anything still going stays listed. */
  const clear = (): void => setItems((prev) => prev.filter((i) => i.state === 'WAITING' || i.state === 'UPLOADING'))

  return { items, add, busy, clear }
}

const sizeLabel = (bytes: number): string =>
  bytes < 1024 ? `${bytes} B`
  : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB`
  : `${(bytes / 1024 / 1024).toFixed(1)} MB`

const STATE_WORDS: Readonly<Record<UploadState, string>> = {
  WAITING: 'Waiting',
  UPLOADING: 'Uploading…',
  DONE: 'Uploaded',
  FAILED: 'Not uploaded',
}

/** One line per file in the batch, with how it went. */
export const UploadQueueList = ({ items, onClear }: { items: readonly UploadItem[]; onClear: () => void }) => {
  if (items.length === 0) return null
  const done = items.filter((i) => i.state === 'DONE').length
  const failed = items.filter((i) => i.state === 'FAILED').length
  const going = items.length - done - failed
  return (
    <div className="upload-queue" aria-label="Uploads" role="region">
      <div className="upload-queue__head">
        <span>
          {done} of {items.length} uploaded{failed > 0 ? ` · ${failed} not uploaded` : ''}
        </span>
        {going === 0 ? (
          <button type="button" className="link-button" onClick={onClear}>Clear this list</button>
        ) : null}
      </div>
      <ul className="upload-queue__list">
        {items.map((item) => (
          <li key={item.id} className={`upload-queue__item upload-queue__item--${item.state.toLowerCase()}`}>
            <span className="upload-queue__name">{item.name}</span>
            <span className="upload-queue__size num">{sizeLabel(item.size)}</span>
            <span className="upload-queue__state">
              {STATE_WORDS[item.state]}{item.error ? ` — ${item.error}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Somewhere to drop files. Wraps the drive's file list so the whole panel is
 * the target, and says so while something is being dragged over it.
 */
export const DropZone = ({ onFiles, children, label = 'Drop files here to upload them' }: {
  onFiles: (files: File[]) => void
  children: ReactNode
  label?: string
}) => {
  const [over, setOver] = useState(false)
  const carriesFiles = (e: DragEvent): boolean => Array.from(e.dataTransfer?.types ?? []).includes('Files')
  return (
    <div
      className={over ? 'drop-zone drop-zone--over' : 'drop-zone'}
      data-testid="drop-zone"
      onDragOver={(e) => {
        if (!carriesFiles(e)) return
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
        setOver(false)
      }}
      onDrop={(e) => {
        const files = Array.from(e.dataTransfer?.files ?? [])
        if (files.length === 0) return
        e.preventDefault()
        setOver(false)
        onFiles(files)
      }}
    >
      {over ? <div className="drop-zone__hint">{label}</div> : null}
      {children}
    </div>
  )
}
